import * as React from 'react';
import {ChangeEvent, forwardRef, KeyboardEvent, useImperativeHandle, useRef} from 'react';

import {Input} from '../Input/Input';

export type InputProperties = {
    autocorrect?: `on` | `off`;
    borderLeft?: boolean;
    borderRight?: boolean;
    btnText?: string | JSX.Element;
    button?: string | JSX.Element;
    className?: string;
    containerClass?: string;
    defaultValue?: string;
    disabled?: boolean;
    displayCounter?: boolean;
    e2e?: string;
    error?: string;
    inputSize?: `default` | `sm`;
    label?: string;
    maxLength?: number;
    mbUnits?: string;
    name: string;
    onBlur?: (val: ChangeEvent<HTMLInputElement>) => void;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onEnter?: (val: string) => void;
    parentCSS?: string;
    placeholder?: string;
    prepend?: boolean;
    required?: boolean;
    tabIndex?: number;
    title?: string;
    type?: 'number' | 'email' | 'text' | 'hidden' | 'password';
    value?: string;
};

export const InputGroup = forwardRef<HTMLInputElement, InputProperties>(
    (
        {
            autocorrect,
            borderLeft = true,
            borderRight = true,
            btnText,
            button,
            className = ``,
            containerClass,
            defaultValue,
            disabled,
            displayCounter = false,
            e2e,
            error,
            inputSize,
            label,
            maxLength,
            mbUnits = `3`,
            name,
            onBlur,
            onChange,
            onEnter,
            parentCSS,
            placeholder,
            prepend = false,
            required,
            tabIndex,
            title,
            type = `text`,
            value,
        },
        ref,
    ) => {
        const inputRef = useRef<HTMLInputElement>();

        /**
         * Handles onChange for Input
         * @param e
         */
        const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
            // If onChange provided, invoke at this time
            if (onChange) {
                onChange(e);
            }
        };

        /**
         * Handles onKeyDown for Input
         * @param e
         */
        const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
            // If onChange provided, invoke at this time
            if (onEnter && e.key === `Enter`) {
                onEnter(inputRef.current.value);
            }
        };

        useImperativeHandle(ref, () => inputRef.current);

        return (
            <div className={containerClass}>
                <div className={`!tw-mb-${mbUnits} tw-w-full${error ? ' !tw-mb-8' : ''}`}>
                    <div
                        className={`tw-h-[52px] tw-flex-nowrap tw-relative tw-flex tw-items-stretch tw-w-full ${
                            className ? ' ' + className : ''
                        } tw-mb-0`}
                    >
                        {prepend && (
                            <div className="tw-mr-[-1px] tw-flex">
                                <div
                                    className={`tw-flex tw-items-center tw-py-1.5 tw-px-3 tw-mb-0 tw-text-base tw-font-normal tw-leading-normal tw-whitespace-nowrap tw-border ${
                                        disabled ? 'tw-bg-gray-100' : 'tw-bg-white'
                                    }`}
                                >
                                    {btnText}
                                </div>
                            </div>
                        )}
                        <div className={`${parentCSS ? parentCSS + ' ' : ''} tw-w-full${error ? ' tw-mb-0' : ''}`}>
                            <Input
                                autocorrect={autocorrect}
                                className={`${className}`.trim()}
                                clearMargin={true}
                                defaultValue={defaultValue}
                                disabled={disabled}
                                displayCounter={displayCounter}
                                e2e={e2e}
                                error={error}
                                label={label}
                                maxLength={maxLength}
                                name={name}
                                onBlur={(e) => {
                                    if (onBlur) {
                                        onBlur(e);
                                    }
                                }}
                                inputSize={inputSize}
                                onChange={handleOnChange}
                                onKeyDown={handleKeyDown}
                                placeholder={placeholder}
                                ref={inputRef}
                                required={required}
                                style={borderRight === false && !error ? {borderRight: 0} : {}}
                                tabIndex={tabIndex}
                                title={title}
                                type={type}
                                value={value}
                            />
                        </div>
                        {(!prepend || button) && (
                            <div className="tw-mb-0 tw-flex tw-ml-[-1px]">
                                {button ? (
                                    button
                                ) : (
                                    <div
                                        className={`tw-flex tw-items-center tw-py-1.5 tw-px-3 tw-mb-0 tw-text-base tw-font-normal tw-leading-normal tw-whitespace-nowrap tw-border ${
                                            disabled ? 'tw-bg-gray-100' : 'tw-bg-white tw-cursor-pointer'
                                        }`}
                                        style={borderLeft === false ? {borderLeft: 0} : {}}
                                    >
                                        {btnText}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    },
);
